// Consola SQL — Sistemas → Gestión Base de Datos. "Mini pgAdmin".
// Ejecuta UNA sentencia contra PostgREST vía RPC autenticacion.admin_ejecutar_sql.
// Seguridad (en la BD): solo perfil 'administrador'; corre como app_user →
// respeta RLS y la empresa en sesión; permite SELECT/WITH/VALUES/TABLE/EXPLAIN
// e INSERT/UPDATE/DELETE; bloquea DDL (CREATE/ALTER/DROP/TRUNCATE); audita todo.
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Database, Loader2, Pencil, Play, Table2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

type Fila = Record<string, unknown>;
interface Resultado {
  ok: boolean; tipo?: 'consulta' | 'modificacion';
  filas?: Fila[]; total?: number; truncado?: boolean; limite?: number;
  afectadas?: number; error?: string; sqlstate?: string; ms?: number;
}
interface TablaMeta { esquema: string; tabla: string; tipo: string }
interface Editable { schema: string; tabla: string; pk: string[] }

const HIST_KEY = 'consola_sql_historial';
const cargarHist = (): string[] => { try { return JSON.parse(localStorage.getItem(HIST_KEY) || '[]'); } catch { return []; } };

// Detecta si un SELECT es de UNA sola tabla (habilita edición inline).
function detectarTabla(sql: string): { schema: string; tabla: string } | null {
  const s = sql.trim().replace(/\s+/g, ' ');
  if (!/^select\b/i.test(s)) return null;
  if (/\bjoin\b|\bunion\b|\bgroup\s+by\b|\bdistinct\b/i.test(s)) return null;
  const m = s.match(/\bfrom\s+"?([a-z_][\w]*)"?\."?([a-z_][\w]*)"?/i);
  if (!m) return null;
  const from = s.slice(s.toLowerCase().indexOf(' from ') + 6);
  const clausula = from.split(/\bwhere\b|\border\b|\blimit\b|\bgroup\b|\bhaving\b|\boffset\b/i)[0];
  if (clausula.includes(',')) return null; // más de una tabla
  return { schema: m[1], tabla: m[2] };
}

export function ConsolaSQL() {
  const [sql, setSql] = useState('');
  const [res, setRes] = useState<Resultado | null>(null);
  const [cargando, setCargando] = useState(false);
  const [hist, setHist] = useState<string[]>(cargarHist);
  const [panel, setPanel] = useState(true);
  const [edit, setEdit] = useState<Editable | null>(null);
  const [celdaEdit, setCeldaEdit] = useState<{ row: number; col: string } | null>(null);
  const [valorEdit, setValorEdit] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);

  const { data: tablas = [] } = useQuery<TablaMeta[]>({
    queryKey: ['consola-sql-tablas'],
    queryFn: async () => (await apiClient.post('/rpc/admin_sql_tablas', {})).data || [],
    staleTime: 300_000,
  });
  const porEsquema = useMemo(() => {
    const m = new Map<string, TablaMeta[]>();
    for (const t of tablas) { const a = m.get(t.esquema) || []; a.push(t); m.set(t.esquema, a); }
    return Array.from(m.entries());
  }, [tablas]);

  const ejecutar = async () => {
    const q = sql.trim();
    if (!q) { toast.error('Escribe una sentencia'); return; }
    setCargando(true); setRes(null); setEdit(null); setCeldaEdit(null);
    try {
      const { data } = await apiClient.post<Resultado>('/rpc/admin_ejecutar_sql', { _sql: q });
      setRes(data);
      // guardar en historial (solo las últimas 20, sin duplicar la última)
      setHist((h) => { const nh = [q, ...h.filter((x) => x !== q)].slice(0, 20); localStorage.setItem(HIST_KEY, JSON.stringify(nh)); return nh; });
      if (data.ok && data.tipo === 'modificacion') toast.success(`${data.afectadas} fila(s) afectada(s)`);
      if (!data.ok) toast.error('La sentencia devolvió un error');
      // ¿el resultado es editable? (una sola tabla + PK presente)
      if (data.ok && data.tipo === 'consulta' && data.filas?.length) {
        const t = detectarTabla(q);
        if (t) {
          try {
            const { data: pk } = await apiClient.post<string[]>('/rpc/admin_sql_pk', { _schema: t.schema, _tabla: t.tabla });
            if (Array.isArray(pk) && pk.length) setEdit({ schema: t.schema, tabla: t.tabla, pk });
          } catch { /* sin PK → solo lectura */ }
        }
      }
    } catch (e) {
      setRes({ ok: false, error: getErrorMessage(e) });
    } finally {
      setCargando(false);
    }
  };

  const guardarCelda = async (rowIdx: number, col: string) => {
    if (!edit || !res?.filas) { setCeldaEdit(null); return; }
    const row = res.filas[rowIdx];
    const original = row[col];
    if (String(original ?? '') === valorEdit) { setCeldaEdit(null); return; } // sin cambios
    const pkObj: Record<string, string> = {};
    for (const k of edit.pk) pkObj[k] = String(row[k] ?? '');
    try {
      const { data } = await apiClient.post<{ ok: boolean; afectadas?: number; error?: string }>(
        '/rpc/admin_actualizar_celda',
        { _schema: edit.schema, _tabla: edit.tabla, _columna: col, _valor: valorEdit, _pk: pkObj, _nulo: false });
      if (data.ok) {
        setRes((r) => {
          if (!r?.filas) return r;
          const filas = r.filas.map((f, i) => (i === rowIdx ? { ...f, [col]: valorEdit } : f));
          return { ...r, filas };
        });
        toast.success(data.afectadas === 1 ? 'Celda actualizada' : `${data.afectadas} fila(s) afectada(s)`);
      } else {
        toast.error(data.error || 'No se pudo actualizar');
      }
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setCeldaEdit(null);
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); void ejecutar(); }
  };

  const usarTabla = (t: TablaMeta) => {
    setSql(`SELECT * FROM ${t.esquema}.${t.tabla} LIMIT 100`);
    taRef.current?.focus();
  };

  const columnas = useMemo(() => (res?.filas?.length ? Object.keys(res.filas[0]) : []), [res]);
  const editable = !!edit && edit.pk.length > 0 && edit.pk.every((k) => columnas.includes(k));
  const cel = (v: unknown) => (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

  // Exportar el resultado actual
  const expFilas: Celda[][] = useMemo(() => {
    if (!res?.filas?.length) return [];
    return res.filas.map((f, i): Celda[] => [i + 1, ...columnas.map((c) => cel(f[c]))]);
  }, [res, columnas]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Database className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Consola SQL</h1>
          <p className="text-sm text-muted-foreground">Gestión de Base de Datos — ejecución de sentencias</p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-200">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Solo <b>administrador</b>. Se ejecuta con tus permisos y <b>respeta el aislamiento por empresa (RLS)</b>.
          Permitido: <b>SELECT/WITH/EXPLAIN</b> e <b>INSERT/UPDATE/DELETE</b>. Bloqueado: DDL (CREATE/ALTER/DROP/TRUNCATE).
          Una sola sentencia por ejecución. Todo queda auditado.
        </span>
      </div>

      <div className={`grid grid-cols-1 gap-4 ${panel ? 'lg:grid-cols-[240px_minmax(0,1fr)]' : ''}`}>
        {/* Panel de tablas (colapsable) */}
        {panel && (
        <Card className="order-2 lg:order-1 min-w-0">
          <CardContent className="p-3 max-h-[70vh] overflow-y-auto">
            <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1"><Table2 className="w-3.5 h-3.5" /> Tablas ({tablas.length})</p>
            <div className="space-y-2">
              {porEsquema.map(([esq, ts]) => (
                <details key={esq} open={porEsquema.length <= 3}>
                  <summary className="cursor-pointer text-xs font-medium text-primary">{esq} <span className="text-muted-foreground">({ts.length})</span></summary>
                  <ul className="mt-1 space-y-0.5 pl-2">
                    {ts.map((t) => (
                      <li key={`${t.esquema}.${t.tabla}`}>
                        <button className="w-full truncate text-left text-xs py-0.5 px-1 rounded hover:bg-muted"
                          title={`${t.esquema}.${t.tabla}`} onClick={() => usarTabla(t)}>
                          {t.tabla}{t.tipo === 'VIEW' ? ' ◦' : ''}
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Editor + resultados */}
        <div className="order-1 lg:order-2 space-y-3 min-w-0">
          <Card>
            <CardContent className="p-3 space-y-2">
              <textarea
                ref={taRef}
                className="w-full h-40 rounded-md border border-input bg-background p-3 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="SELECT * FROM financiero.partner LIMIT 100"
                spellCheck={false}
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                onKeyDown={onKey}
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setPanel((p) => !p)} title="Mostrar/ocultar tablas">
                    <Table2 className="w-4 h-4 mr-1" /> {panel ? 'Ocultar tablas' : 'Tablas'}
                  </Button>
                  <span className="text-xs text-muted-foreground hidden sm:inline">Ctrl/⌘ + Enter para ejecutar</span>
                </div>
                <div className="flex items-center gap-2">
                  {sql && <Button size="sm" variant="ghost" onClick={() => setSql('')}><Trash2 className="w-4 h-4" /></Button>}
                  <Button size="sm" onClick={() => void ejecutar()} isLoading={cargando}>
                    {!cargando && <Play className="w-4 h-4 mr-1" />} Ejecutar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Historial */}
          {hist.length > 0 && !res && (
            <Card><CardContent className="p-3">
              <p className="text-xs font-semibold text-muted-foreground mb-1">Historial reciente</p>
              <ul className="space-y-0.5">
                {hist.slice(0, 8).map((h, i) => (
                  <li key={i}><button className="w-full truncate text-left text-xs font-mono py-0.5 px-1 rounded hover:bg-muted" title={h} onClick={() => setSql(h)}>{h}</button></li>
                ))}
              </ul>
            </CardContent></Card>
          )}

          {/* Resultado */}
          {cargando && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}

          {res && !cargando && (
            <Card><CardContent className="p-3">
              {!res.ok ? (
                <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
                  <p className="font-semibold">Error{res.sqlstate ? ` (${res.sqlstate})` : ''}</p>
                  <p className="font-mono text-xs mt-1 whitespace-pre-wrap">{res.error}</p>
                </div>
              ) : res.tipo === 'modificacion' ? (
                <p className="text-sm"><b>{res.afectadas}</b> fila(s) afectada(s) · <span className="text-muted-foreground">{res.ms} ms</span></p>
              ) : (
                <>
                  <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">
                      {res.total} fila(s) · {res.ms} ms
                      {res.truncado && <span className="text-amber-600 dark:text-amber-400"> · mostrando las primeras {res.limite}</span>}
                      {editable
                        ? <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Pencil className="w-3 h-3" /> Edición habilitada · doble clic en una celda ({edit!.schema}.{edit!.tabla})</span>
                        : res.filas?.length ? <span className="ml-2 text-muted-foreground">· solo lectura</span> : null}
                    </span>
                    {!!res.filas?.length && (
                      <ExportarBotones nombre={`consulta-${fechaLocal()}`} titulo="Resultado consulta"
                        headers={['N°', ...columnas]} filas={expFilas} colsDerecha={[0]} />
                    )}
                  </div>
                  {res.filas?.length ? (
                    <div className="overflow-auto max-h-[60vh] border rounded-md">
                      <table className="text-xs border-collapse min-w-full">
                        <thead><tr className="border-b text-left text-muted-foreground bg-muted sticky top-0 z-10">
                          <th className="py-1.5 px-3">#</th>
                          {columnas.map((c) => <th key={c} className="py-1.5 px-3 whitespace-nowrap font-medium">{c}</th>)}
                        </tr></thead>
                        <tbody>
                          {res.filas.map((f, i) => (
                            <tr key={i} className="border-b last:border-0 hover:bg-muted/40 align-top">
                              <td className="py-1 px-3 text-muted-foreground tabular-nums whitespace-nowrap">{i + 1}</td>
                              {columnas.map((c) => {
                                const esPk = edit?.pk.includes(c);
                                const editandoEsta = editable && !esPk;
                                const enEdicion = celdaEdit?.row === i && celdaEdit?.col === c;
                                return (
                                  <td key={c}
                                    className={`py-1 px-3 font-mono whitespace-pre-wrap min-w-[80px] max-w-[420px] break-words ${editandoEsta ? 'cursor-pointer' : ''} ${esPk ? 'text-muted-foreground' : ''}`}
                                    title={editandoEsta ? 'Doble clic para editar' : esPk ? 'Clave primaria (no editable)' : undefined}
                                    onDoubleClick={() => { if (editandoEsta) { setCeldaEdit({ row: i, col: c }); setValorEdit(cel(f[c])); } }}>
                                    {enEdicion ? (
                                      <input autoFocus
                                        className="w-full min-w-[120px] rounded border border-primary bg-background px-1 py-0.5 text-xs"
                                        value={valorEdit}
                                        onChange={(e) => setValorEdit(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void guardarCelda(i, c); } else if (e.key === 'Escape') { setCeldaEdit(null); } }}
                                        onBlur={() => void guardarCelda(i, c)} />
                                    ) : cel(f[c])}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="py-6 text-center text-sm text-muted-foreground">La consulta no devolvió filas</p>}
                </>
              )}
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}
